import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Pencil, Plus, Trash2, X, Key, UserCheck, UserX, ShieldCheck, User,
} from "lucide-react";
import { API, authHeaders, formatApiErrorDetail } from "./api";
import { AField, ASelect, SectionShell, SaveButton, Notice } from "./ui";

const ROLES = [
  { value: "admin", label: "Admin" },
  { value: "staff", label: "Staff" },
];

const EMPTY_NEW = { name: "", email: "", role: "staff", password: "" };

const fmtDate = (iso) => {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-");
  return y && m && d ? `${d}/${m}/${y}` : s;
};

const RoleChip = ({ role }) => {
  if (role === "admin") {
    return (
      <span className="inline-flex items-center gap-1.5 text-[10.5px] font-bold tracking-[0.14em] uppercase px-2.5 py-1 border bg-forest text-cream border-forest">
        <ShieldCheck size={11} strokeWidth={1.8} /> Admin
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-[10.5px] font-bold tracking-[0.14em] uppercase px-2.5 py-1 border bg-cream-3 text-forest/80 border-forest/15">
      <User size={11} strokeWidth={1.8} /> Staff
    </span>
  );
};

const StatusChip = ({ active }) => (
  <span
    data-testid={`user-status-${active ? "active" : "deactivated"}`}
    className={`inline-flex items-center gap-1.5 text-[10.5px] font-bold tracking-[0.14em] uppercase px-2.5 py-1 border ${
      active
        ? "bg-gold-soft/25 text-gold-dark border-gold/40"
        : "bg-red-50 text-red-700 border-red-300"
    }`}
  >
    {active ? <UserCheck size={11} strokeWidth={1.8} /> : <UserX size={11} strokeWidth={1.8} />}
    {active ? "Active" : "Deactivated"}
  </span>
);

const PasswordResetModal = ({ user, onClose, onSaved }) => {
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) return setErr("Password must be at least 8 characters.");
    if (pw !== confirm) return setErr("Passwords do not match.");
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/users/${user.id}/password`,
        { new_password: pw },
        authHeaders()
      );
      toast.success(`Password reset for ${user.name}.`);
      onSaved();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Could not reset password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      data-testid="user-password-modal"
    >
      <div className="absolute inset-0 bg-ink/60 backdrop-blur-sm" onClick={onClose} />
      <form
        onSubmit={submit}
        className="relative bg-cream border border-forest/20 w-full max-w-md p-7 shadow-2xl"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 p-2 text-forest/60 hover:text-forest"
        >
          <X size={18} strokeWidth={1.8} />
        </button>
        <div className="flex items-center gap-3 mb-5">
          <span className="w-10 h-10 rounded-full border border-gold flex items-center justify-center">
            <Key size={16} strokeWidth={1.6} className="text-gold-dark" />
          </span>
          <div>
            <p className="eyebrow text-gold-dark !mb-0.5">Reset Password</p>
            <h3 className="font-serif text-xl text-forest leading-tight">{user.name}</h3>
          </div>
        </div>
        <p className="text-[12.5px] text-sage mb-5 leading-relaxed">
          Set a new temporary password. The user can change it from Settings after signing in.
        </p>
        {err && (
          <p className="bg-red-50 border border-red-200 text-red-700 text-[13px] px-3 py-2 mb-4">
            {err}
          </p>
        )}
        <AField
          label="New Password (min 8 characters)"
          testid="user-reset-password-input"
          type="password"
          required
          minLength={8}
          value={pw}
          onChange={(e) => setPw(e.target.value)}
        />
        <div className="mt-4">
          <AField
            label="Confirm New Password"
            testid="user-reset-password-confirm"
            type="password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>
        <div className="flex gap-3 mt-6">
          <button
            type="submit"
            disabled={saving}
            data-testid="user-reset-password-submit"
            className="btn-gold !py-3 disabled:opacity-60"
          >
            {saving ? "Saving..." : "Reset Password"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost-dark"
            data-testid="user-reset-password-cancel"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export const UsersSection = ({ currentUser }) => {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | user
  const [form, setForm] = useState(EMPTY_NEW);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [resetFor, setResetFor] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/users`, authHeaders());
      setList(data || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not load users");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(EMPTY_NEW);
    setEditing("new");
    setError("");
    setSuccess("");
  };

  const openEdit = (u) => {
    setForm({ name: u.name, role: u.role, active: !!u.active });
    setEditing(u);
    setError("");
    setSuccess("");
  };

  const close = () => {
    setEditing(null);
    setForm(EMPTY_NEW);
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setSaving(true);
    try {
      if (editing === "new") {
        await axios.post(
          `${API}/admin/users`,
          {
            name: form.name.trim(),
            email: form.email.trim().toLowerCase(),
            role: form.role,
            password: form.password,
          },
          authHeaders()
        );
        setSuccess("User created — they can sign in at /admin now.");
      } else {
        await axios.put(
          `${API}/admin/users/${editing.id}`,
          {
            name: form.name.trim(),
            role: form.role,
            active: !!form.active,
          },
          authHeaders()
        );
        setSuccess("User updated.");
      }
      await load();
      close();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u) => {
    const nextActive = !u.active;
    const verb = nextActive ? "Reactivate" : "Deactivate";
    if (!window.confirm(`${verb} ${u.name}? ${nextActive ? "They will be able to sign in again." : "They will no longer be able to sign in."}`)) return;
    try {
      await axios.put(
        `${API}/admin/users/${u.id}`,
        { name: u.name, role: u.role, active: nextActive },
        authHeaders()
      );
      await load();
      toast.success(`${u.name} ${nextActive ? "reactivated" : "deactivated"}.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${u.id}`, authHeaders());
      await load();
      toast.success(`${u.name} deleted.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete");
    }
  };

  const sorted = useMemo(
    () =>
      (list || [])
        .slice()
        .sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0)),
    [list]
  );

  if (list === null) return <p className="text-sm text-sage">Loading…</p>;

  const isSelf = (u) => currentUser && u.id === currentUser.id;

  return (
    <SectionShell
      title="Users"
      subtitle="Who can sign in at /admin. Admins manage everything; Staff can manage content, enquiries and invoices but not Users or Settings."
      testid="admin-users-section"
      action={
        !editing && (
          <button
            type="button"
            data-testid="users-new-button"
            onClick={openNew}
            className="btn-gold"
          >
            <Plus size={14} strokeWidth={2.2} /> New User
          </button>
        )
      }
    >
      <Notice error={error} success={success} />

      {!editing && (
        <>
        <div className="hidden md:block bg-white border border-forest/10">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-forest/10 text-[10.5px] tracking-[0.18em] uppercase text-forest/60">
                <th className="text-left px-5 py-3 font-bold">Name</th>
                <th className="text-left px-4 py-3 font-bold">Email</th>
                <th className="text-left px-4 py-3 font-bold">Role</th>
                <th className="text-left px-4 py-3 font-bold">Status</th>
                <th className="text-left px-4 py-3 font-bold">Added</th>
                <th className="w-48 px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((u, i) => {
                const self = isSelf(u);
                return (
                  <tr
                    key={u.id}
                    data-testid={`user-row-${i + 1}`}
                    className="border-b border-forest/5 last:border-b-0 hover:bg-cream-3/50 transition-colors"
                  >
                    <td className="px-5 py-4">
                      <p className="font-semibold text-forest">
                        {u.name}
                        {self && (
                          <span className="ml-2 text-[10px] tracking-[0.2em] uppercase text-gold-dark font-bold">
                            (you)
                          </span>
                        )}
                      </p>
                    </td>
                    <td className="px-4 py-4 text-sage">{u.email}</td>
                    <td className="px-4 py-4">
                      <RoleChip role={u.role} />
                    </td>
                    <td className="px-4 py-4">
                      <StatusChip active={u.active} />
                    </td>
                    <td className="px-4 py-4 text-[13px] text-sage">
                      {fmtDate(u.created_at)}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex justify-end gap-1 flex-wrap">
                        <button
                          type="button"
                          onClick={() => openEdit(u)}
                          data-testid={`user-edit-${i + 1}`}
                          className="p-2.5 text-forest/70 hover:text-forest transition-colors"
                          title="Edit"
                        >
                          <Pencil size={16} strokeWidth={1.6} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setResetFor(u)}
                          data-testid={`user-reset-${i + 1}`}
                          className="p-2.5 text-forest/70 hover:text-forest transition-colors"
                          title="Reset password"
                        >
                          <Key size={16} strokeWidth={1.6} />
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleActive(u)}
                          disabled={self}
                          data-testid={`user-toggle-${i + 1}`}
                          className="p-2.5 text-forest/70 hover:text-forest transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          title={self ? "You cannot deactivate yourself" : u.active ? "Deactivate" : "Reactivate"}
                        >
                          {u.active ? (
                            <UserX size={16} strokeWidth={1.6} />
                          ) : (
                            <UserCheck size={16} strokeWidth={1.6} />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(u)}
                          disabled={self}
                          data-testid={`user-delete-${i + 1}`}
                          className="p-2.5 text-forest/60 hover:text-red-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          title={self ? "You cannot delete yourself" : "Delete"}
                        >
                          <Trash2 size={16} strokeWidth={1.6} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Mobile cards */}
        <div className="md:hidden space-y-3">
          {sorted.map((u, i) => {
            const self = isSelf(u);
            return (
              <div key={u.id} data-testid={`user-card-${i + 1}`} className="bg-white border border-forest/10 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-forest">
                      {u.name}
                      {self && <span className="ml-2 text-[10px] tracking-[0.2em] uppercase text-gold-dark font-bold">(you)</span>}
                    </p>
                    <p className="text-[12px] text-sage mt-1 break-all">{u.email}</p>
                    <p className="text-[11px] text-sage mt-1">Added {fmtDate(u.created_at)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <RoleChip role={u.role} />
                      <StatusChip active={u.active} />
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t border-forest/8 justify-end">
                  <button type="button" onClick={() => openEdit(u)} className="p-2 text-forest/70 hover:text-forest" title="Edit"><Pencil size={16} strokeWidth={1.6} /></button>
                  <button type="button" onClick={() => setResetFor(u)} className="p-2 text-forest/70 hover:text-forest" title="Reset password"><Key size={16} strokeWidth={1.6} /></button>
                  <button type="button" onClick={() => toggleActive(u)} disabled={self} className="p-2 text-forest/70 hover:text-forest disabled:opacity-30 disabled:cursor-not-allowed" title={self ? "You cannot deactivate yourself" : u.active ? "Deactivate" : "Reactivate"}>
                    {u.active ? <UserX size={16} strokeWidth={1.6} /> : <UserCheck size={16} strokeWidth={1.6} />}
                  </button>
                  <button type="button" onClick={() => remove(u)} disabled={self} className="p-2 text-forest/60 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed" title={self ? "You cannot delete yourself" : "Delete"}><Trash2 size={16} strokeWidth={1.6} /></button>
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}

      {editing && (
        <form
          onSubmit={save}
          className="bg-white border border-forest/10 p-6 sm:p-8 space-y-5 max-w-2xl"
          data-testid="user-form"
        >
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-xl text-forest">
              {editing === "new" ? "New User" : `Edit ${editing.name}`}
            </h3>
            <button
              type="button"
              onClick={close}
              className="p-2 text-forest/60 hover:text-forest"
              data-testid="user-close-form"
              aria-label="Close editor"
            >
              <X size={18} strokeWidth={1.8} />
            </button>
          </div>

          <AField
            label="Full Name"
            testid="user-field-name"
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />

          {editing === "new" && (
            <AField
              label="Email"
              testid="user-field-email"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              hint="This becomes their sign-in email at /admin."
            />
          )}

          <ASelect
            label="Role"
            testid="user-field-role"
            value={form.role}
            onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
            options={ROLES}
          />

          {editing === "new" && (
            <AField
              label="Temporary Password (min 8 characters)"
              testid="user-field-password"
              type="password"
              required
              minLength={8}
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              hint="Share this with the user securely. They can change it in Settings after signing in."
            />
          )}

          {editing !== "new" && (
            <label className="flex items-center gap-3 pt-2">
              <input
                type="checkbox"
                data-testid="user-field-active"
                checked={!!form.active}
                disabled={isSelf(editing)}
                onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                className="h-5 w-5 accent-forest disabled:opacity-40"
              />
              <span className="text-[13px] font-semibold text-forest">
                Active (can sign in)
                {isSelf(editing) && (
                  <span className="ml-2 text-[11px] text-sage font-normal">
                    — you cannot deactivate yourself
                  </span>
                )}
              </span>
            </label>
          )}

          <div className="flex gap-3">
            <SaveButton saving={saving} testid="user-save-button" />
            <button
              type="button"
              onClick={close}
              className="btn-ghost-dark"
              data-testid="user-cancel-button"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {resetFor && (
        <PasswordResetModal
          user={resetFor}
          onClose={() => setResetFor(null)}
          onSaved={() => setResetFor(null)}
        />
      )}
    </SectionShell>
  );
};
